<template>
    <LoadingPage v-if="disableSendingButton || isLoadingContractDetails"></LoadingPage>

    <div class="row text-center justify-content-center">
        <h2 class="modal-title"><strong>Send Contract</strong></h2>
    </div>

    <div class="row mt-2 mb-2 text-center">
        <h6 class="card-subtitle text-muted">Date: <strong>{{ currentDate }}</strong></h6>
    </div>

    <form ref="contractFormRef" class="row g-0 mx-4" style="gap: 0.5rem" @submit.prevent="sendContract">
        <div class="mb-3">
            <label for="contractTitle" class="form-label">Contract Title<span class="text-danger">*</span> :</label>
            <input type="text" id="contractTitle" class="form-control" v-model="contractTitle" placeholder="Enter contract title" required>
        </div>

        <div class="mb-3">
            <label for="sentTo" class="form-label">To (Signers)<span class="text-danger">*</span> :</label>
            <div class="email-input-container">
                <div v-for="(recipient, index) in sentToRecipients" :key="index" class="email-pill">
                    <span>{{ recipient.displayName || recipient.email }}</span>
                    <button type="button" class="email-pill-remove" @click="removeRecipient(index, 'sentTo')">×</button>
                </div>
                <input type="text" class="email-input-field" v-model="currentSentToInput"
                    @keydown.enter.prevent="addRecipient('sentTo')" @keydown.tab.prevent="addRecipient('sentTo')"
                    @blur="addRecipient('sentTo')" @paste="handlePaste('sentTo', $event)" placeholder="Add recipient">
            </div>
        </div>

        <div class="mb-3">
            <label for="cc" class="form-label">CC:</label>
            <div class="email-input-container">
                <div v-for="(recipient, index) in ccRecipients" :key="index" class="email-pill">
                    <span>{{ recipient.displayName || recipient.email }}</span>
                    <button type="button" class="email-pill-remove" @click="removeRecipient(index, 'cc')">×</button>
                </div>
                <input type="text" class="email-input-field" v-model="currentCcInput"
                    @keydown.enter.prevent="addRecipient('cc')" @keydown.tab.prevent="addRecipient('cc')"
                    @blur="addRecipient('cc')" @paste="handlePaste('cc', $event)" placeholder="Add CC recipient">
            </div>
        </div>

        <div class="mb-3">
            <label for="customMessage" class="form-label">Custom Message:</label>
            <textarea class="form-control" id="customMessage" v-model="customMessage" rows="4" maxlength="1000"
                placeholder="Enter an optional message..."></textarea>
            <div class="form-text text-end">{{ customMessage.length }} / 1000</div>
        </div>

        <div class="pt-3 pb-3 justify-content-center d-flex">
            <button type="button" class="btn btn-info text-light" @click="sendContract"
                :disabled="disableFormButton() || disableSendingButton">
                <i class="fas fa-paper-plane me-2"></i> Send
            </button>
        </div>
    </form>
</template>

<script>
import { log, isValidEmail, cleanEmailString, extractEmails, parseEmailRecipient } from '@/plugins/helper.js'
import LoadingPage from '@/components/Utilities/vertex-loading.vue'

export default {
    name: 'SendForm',
    components: {
        LoadingPage
    },

    props: {
        initialSendTo: {
            type: [Array, String, Object],
            default: () => []
        },
        contractID: {
            type: [String, Number],
            default: ''
        },
        hasEditor: {
            type: Boolean,
            default: false
        },
        initialContractTitle: {
            type: String,
            default: ''
        },
        contractData: {
            type: Object,
            default: () => null
        }
    },

    emits: ["contractSentSuccess", "close"],

    data() {
        return {
            currentDate: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
            contractTitle: this.initialContractTitle || '',
            sentToRecipients: [],
            ccRecipients: [],
            currentSentToInput: '',
            currentCcInput: '',
            customMessage: '',
            disableSendingButton: false,
            isLoadingContractDetails: false
        };
    },

    methods: {
        isEmailValid(email) {
            return isValidEmail(email);
        },

        parseEmailInput(input) {
            if (!input) return null;
            if (typeof input === 'object') {
                if (input.email && this.isEmailValid(input.email)) {
                    const first = input.first_name || input.firstName || '';
                    const last = input.last_name || input.lastName || '';
                    const full = input.displayName || input.name || [first, last].filter(Boolean).join(' ') || input.email;
                    return {
                        displayName: full,
                        email: input.email.trim(),
                        first_name: first,
                        last_name: last,
                        has_signed: !!(input.has_signed || input.signed_at),
                        signed_at: input.signed_at || null,
                        isSigner: true
                    };
                }
                return null;
            }

            const parsed = parseEmailRecipient(input);
            if (parsed && this.isEmailValid(parsed.email)) {
                return {
                    displayName: parsed.displayName,
                    email: parsed.email.trim(),
                    has_signed: false,
                    signed_at: null,
                    isSigner: false
                };
            }
            return null;
        },

        addRecipient(field) {
            let inputField = field === 'sentTo' ? this.currentSentToInput : this.currentCcInput;
            let recipientArray = field === 'sentTo' ? this.sentToRecipients : this.ccRecipients;

            if (!inputField || !inputField.trim()) return;

            const extracted = extractEmails(inputField);
            if (extracted.length > 0) {
                extracted.forEach(email => {
                    const parsed = this.parseEmailInput(email) || { displayName: email, email: email.trim() };
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = recipientArray.some(r => r.email.toLowerCase() === parsed.email.toLowerCase());
                        if (!exists) {
                            recipientArray.push(parsed);
                        }
                    }
                });
            } else {
                const rawInputs = inputField.split(/[,;]/).map(s => s.trim()).filter(Boolean);
                rawInputs.forEach(rawInput => {
                    const parsed = this.parseEmailInput(rawInput);
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = recipientArray.some(r => r.email.toLowerCase() === parsed.email.toLowerCase());
                        if (!exists) {
                            recipientArray.push(parsed);
                        }
                    }
                });
            }

            if (field === 'sentTo') {
                this.currentSentToInput = '';
            } else {
                this.currentCcInput = '';
            }
        },

        removeRecipient(index, field) {
            if (field === 'sentTo') {
                this.sentToRecipients.splice(index, 1);
            } else if (field === 'cc') {
                this.ccRecipients.splice(index, 1);
            }
        },

        handlePaste(field, event) {
            event.preventDefault();
            const pastedText = (event.clipboardData || window.clipboardData)?.getData('text') || '';
            const emails = extractEmails(pastedText);

            let targetRecipients = field === 'sentTo' ? this.sentToRecipients : this.ccRecipients;

            if (emails.length > 0) {
                emails.forEach(email => {
                    const parsed = this.parseEmailInput(email) || { displayName: email, email: email.trim() };
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = targetRecipients.some(r => r.email.toLowerCase() === parsed.email.toLowerCase());
                        if (!exists) {
                            targetRecipients.push(parsed);
                        }
                    }
                });
                if (field === 'sentTo') this.currentSentToInput = '';
                else this.currentCcInput = '';
            } else {
                const cleaned = cleanEmailString(pastedText);
                if (field === 'sentTo') this.currentSentToInput = cleaned || pastedText;
                else this.currentCcInput = cleaned || pastedText;
            }
        },

        extractSigners(contract) {
            if (!contract) return [];
            const signers = [];
            const seenEmails = new Set();

            const addSigner = (signerObj) => {
                if (!signerObj || !signerObj.email) return;
                const cleanEmail = signerObj.email.trim().toLowerCase();
                if (!cleanEmail || !this.isEmailValid(cleanEmail) || seenEmails.has(cleanEmail)) return;
                seenEmails.add(cleanEmail);

                const first = signerObj.first_name || signerObj.firstName || '';
                const last = signerObj.last_name || signerObj.lastName || '';
                const full = [first, last].filter(Boolean).join(' ') || signerObj.name || signerObj.displayName || '';

                signers.push({
                    displayName: full || signerObj.email,
                    first_name: first,
                    last_name: last,
                    name: full,
                    email: signerObj.email.trim(),
                    has_signed: !!(signerObj.has_signed || signerObj.signed_at),
                    signed_at: signerObj.signed_at || null,
                    isSigner: true
                });
            };

            // 1. Contract Acceptance signers
            if (contract.contract_acceptance?.signers?.length) {
                contract.contract_acceptance.signers.forEach(s => addSigner(s));
            }
            if (contract.contract_acceptances?.length) {
                contract.contract_acceptances.forEach(acc => {
                    if (acc.signers?.length) {
                        acc.signers.forEach(s => addSigner(s));
                    }
                });
            }

            // 2. Shared Users (share_type === 'signer')
            if (contract.shared_users?.length) {
                contract.shared_users.forEach(su => {
                    if (su.share_type === 'signer' && su.email) {
                        const hasSigned = !!(contract.is_accepted || contract.contract_status === 4 || contract.contract_status === 7);
                        addSigner({
                            first_name: su.first_name || su.firstName,
                            last_name: su.last_name || su.lastName,
                            name: su.signer_name || [su.first_name, su.last_name].filter(Boolean).join(' '),
                            email: su.email,
                            has_signed: hasSigned,
                            signed_at: hasSigned ? contract.executed_at : null
                        });
                    }
                });
            }

            // 3. Fallback to Lead
            if (signers.length === 0 && contract.Lead?.email) {
                const hasSigned = !!(contract.is_accepted || contract.contract_status === 4 || contract.contract_status === 7);
                const first = contract.Lead.first_name || '';
                const last = contract.Lead.last_name || '';
                addSigner({
                    first_name: first,
                    last_name: last,
                    name: [first, last].filter(Boolean).join(' ') || contract.Lead.name,
                    email: contract.Lead.email,
                    has_signed: hasSigned,
                    signed_at: hasSigned ? contract.executed_at : null
                });
            }

            // 4. Fallback to client_lead_email
            if (signers.length === 0 && contract.client_lead_email) {
                addSigner({
                    email: contract.client_lead_email,
                    displayName: contract.client_lead_email
                });
            }

            return signers;
        },

        updateInitialSendTo(propValue) {
            if (!propValue) return;

            if (Array.isArray(propValue)) {
                if (propValue.length === 0) return;
                propValue.forEach(item => {
                    if (typeof item === 'object' && item !== null) {
                        if (item.email && this.isEmailValid(item.email)) {
                            const exists = this.sentToRecipients.some(r => r.email.toLowerCase() === item.email.trim().toLowerCase());
                            if (!exists) {
                                const first = item.first_name || item.firstName || '';
                                const last = item.last_name || item.lastName || '';
                                const full = item.displayName || item.name || [first, last].filter(Boolean).join(' ') || item.email;
                                this.sentToRecipients.push({
                                    displayName: full,
                                    email: item.email.trim(),
                                    first_name: first,
                                    last_name: last,
                                    has_signed: !!(item.has_signed || item.signed_at),
                                    signed_at: item.signed_at || null,
                                    isSigner: true
                                });
                            }
                        }
                    } else if (typeof item === 'string') {
                        const parsed = this.parseEmailInput(item);
                        if (parsed && this.isEmailValid(parsed.email)) {
                            const exists = this.sentToRecipients.some(r => r.email.toLowerCase() === parsed.email.toLowerCase());
                            if (!exists) {
                                this.sentToRecipients.push({
                                    ...parsed,
                                    isSigner: true
                                });
                            }
                        }
                    }
                });
            } else if (typeof propValue === 'string') {
                const extracted = extractEmails(propValue);
                if (extracted.length > 0) {
                    extracted.forEach(email => {
                        const parsed = this.parseEmailInput(email) || { displayName: email, email: email.trim() };
                        if (parsed && this.isEmailValid(parsed.email)) {
                            const exists = this.sentToRecipients.some(r => r.email.toLowerCase() === parsed.email.toLowerCase());
                            if (!exists) {
                                this.sentToRecipients.push({
                                    ...parsed,
                                    isSigner: true
                                });
                            }
                        }
                    });
                }
            } else if (typeof propValue === 'object') {
                if (propValue.email && this.isEmailValid(propValue.email)) {
                    const exists = this.sentToRecipients.some(r => r.email.toLowerCase() === propValue.email.trim().toLowerCase());
                    if (!exists) {
                        this.sentToRecipients.push({
                            displayName: propValue.displayName || propValue.name || propValue.email,
                            email: propValue.email.trim(),
                            has_signed: !!(propValue.has_signed || propValue.signed_at),
                            isSigner: true
                        });
                    }
                }
            }
        },

        handleContractData(contract) {
            if (!contract) return;
            if (contract.contract_title && !this.initialContractTitle) {
                this.contractTitle = contract.contract_title;
            }

            if (this.sentToRecipients.length === 0) {
                const signers = this.extractSigners(contract);
                if (signers.length > 0) {
                    this.sentToRecipients = signers;
                }
            }
        },

        async fetchContractDetails() {
            const targetContractId = this.contractData?.uuid || this.contractID;
            if (!targetContractId) return;
            this.isLoadingContractDetails = true;
            try {
                const response = await this.$axios.get(`/contracts/${targetContractId}`);
                const contract = response.data.vertex_contract;
                if (contract) {
                    this.handleContractData(contract);
                }
            } catch (err) {
                console.error("Failed to fetch contract details in SendForm:", err);
            } finally {
                this.isLoadingContractDetails = false;
            }
        },

        async sendContract() {
            if (this.hasEditor && this.$parent?.$refs?.editorRef) {
                const editorDirty = this.$parent.$refs.editorRef.isEditorContentDirty?.();
                const discountOk = this.$parent.$refs.contractDetailsFormRef ? this.$parent.$refs.contractDetailsFormRef.isShowDiscountChanged() : true;
                if (editorDirty || discountOk === false) {
                    this.$toast.error("There are unsaved changes.");
                    return;
                }
            }

            if (this.sentToRecipients.length === 0) {
                this.$toast.error("Please add at least one recipient to send the contract.");
                return;
            }

            this.disableSendingButton = true;

            const sentToEmails = this.sentToRecipients.map(recipient => recipient.email);
            const ccEmails = this.ccRecipients.map(recipient => recipient.email);

            const forSending = {
                sent_to: sentToEmails,
                cc: ccEmails,
                bcc: [],
                custom_message: this.customMessage,
                contract_title: this.contractTitle ? this.contractTitle.replace(/<[^>]*>?/gm, '').replace(/[\r\n\t]/g, ' ').trim() : this.contractTitle
            };

            const targetContractId = this.contractData?.uuid || this.contractID;
            log("Contract ID:", targetContractId);
            log("For Sending:", forSending);

            try {
                const response = await this.$axios.post(`/contracts/${targetContractId}/send`, forSending);
                if (response.data.success) {
                    this.$toast.success(response.data.message || "Contract sent successfully");
                    if (this.$parent && 'contract_signer_emails' in this.$parent) {
                        this.$parent.contract_signer_emails = [];
                    }
                    this.$emit("contractSentSuccess");
                    this.resetForm();
                } else {
                    this.$toast.error(response.data.message || "Failed to send contract");
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Failed to send contract");
            } finally {
                this.disableSendingButton = false;
            }
        },

        resetForm() {
            this.sentToRecipients = [];
            this.ccRecipients = [];
            this.currentSentToInput = '';
            this.currentCcInput = '';
            this.customMessage = '';
        },

        disableFormButton() {
            return this.isLoadingContractDetails || this.sentToRecipients.length === 0;
        }
    },

    watch: {
        initialSendTo: {
            handler(newVal) {
                if (newVal) {
                    this.updateInitialSendTo(newVal);
                }
            },
            immediate: true,
            deep: true
        },
        contractData: {
            handler(newVal) {
                if (newVal) {
                    this.handleContractData(newVal);
                }
            },
            immediate: true,
            deep: true
        },
        contractID: {
            handler(newVal) {
                if (newVal && this.sentToRecipients.length === 0) {
                    this.fetchContractDetails();
                }
            },
            immediate: true
        },
        initialContractTitle(newVal) {
            if (newVal) {
                this.contractTitle = newVal;
            }
        }
    },

    created() {
        this.currentDate = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
        if (this.contractData) {
            this.handleContractData(this.contractData);
        }
        if (this.initialSendTo) {
            this.updateInitialSendTo(this.initialSendTo);
        }
    },

    mounted() {
        if (this.sentToRecipients.length === 0 && this.contractID) {
            this.fetchContractDetails();
        }
    }
};
</script>

<style>
/* Container for the pills and input field */
.email-input-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    padding: 0.375rem 0.75rem;
    min-height: 38px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.email-input-container:focus-within {
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Styling for each individual email pill */
.email-pill {
    display: inline-flex;
    align-items: center;
    background-color: #00a0c1;
    border: 1px solid #0087a3;
    border-radius: 16px;
    padding: 0.2rem 0.5rem;
    margin-right: 0.5rem;
    margin-bottom: 0.2rem;
    font-size: 0.9em;
    color: #ffffff;
    white-space: nowrap;
}

.email-pill-remove {
    background: none;
    border: none;
    color: #e0e0e0;
    font-size: 1.1em;
    line-height: 1;
    margin-left: 0.4rem;
    cursor: pointer;
    padding: 0 0.2rem;
    border-radius: 50%;
    transition: background-color 0.2s, color 0.2s;
}

.email-pill-remove:hover {
    background-color: #007c97;
    color: #ffffff;
}

/* Styling for the actual input field within the container */
.email-input-field {
    flex-grow: 1;
    border: none;
    outline: none;
    padding: 0;
    min-width: 80px;
    margin: 0;
    font-size: 1em;
    background: transparent;
}

.email-input-field:focus {
    outline: none;
    box-shadow: none;
}
</style>
